<template>
  <div class="page-container" v-loading="loading">
    <div class="yn-biz-header">
      <span class="yn-biz-title">{{ pageTitle }}</span>
      <div class="header-actions">
        <el-input v-model="keyword" size="mini" placeholder="任务名称/企业" clearable style="width:200px" @keyup.enter.native="load" />
        <el-select v-model="year" size="mini" style="width:100px" @change="load">
          <el-option v-for="y in years" :key="y" :label="y + '年'" :value="y" />
        </el-select>
        <el-button type="primary" size="mini" @click="load">查询</el-button>
      </div>
    </div>

    <el-tabs v-if="showPlanTab" v-model="tab" @tab-click="onTabChange">
      <el-tab-pane label="待我安排" name="pending" />
      <el-tab-pane label="全部任务" name="all" />
    </el-tabs>

    <el-alert
      v-if="tab === 'pending' && !displayRows.length && !loading"
      title="暂无待安排的周期任务"
      type="info"
      :closable="false"
      show-icon
      class="empty-hint"
    />

    <el-table :data="displayRows" border size="small">
      <el-table-column prop="xmbh" label="编号" width="130" />
      <el-table-column prop="xmmc" label="任务名称" min-width="160" />
      <el-table-column prop="wtdw_mc" label="企业" min-width="140" />
      <el-table-column prop="jclx" label="监测类型" width="100" />
      <el-table-column label="状态" width="120">
        <template slot-scope="{ row }">
          <el-tag :type="ztTag(row.xmzt)" size="mini">{{ ztLabel(row.xmzt) }}</el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="assignee_xm" label="接收人" width="90" />
      <el-table-column label="监测条目" min-width="240">
        <template slot-scope="{ row }">
          <div v-for="(it, i) in row.items" :key="i" class="item-line">
            {{ fmtPlace(it) }} · {{ fmtItems(it) }} · {{ freqText(it) }}
          </div>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="200" fixed="right">
        <template slot-scope="{ row }">
          <el-button v-if="canPlanRow(row)" type="text" size="small" @click="goPlan(row)">安排</el-button>
          <el-button v-if="showDispatchLink(row)" type="text" size="small" @click="goPlan(row)">安排/派遣</el-button>
          <el-button type="text" size="small" @click="goHub(row)">任务监控</el-button>
        </template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script>
import api from '@/utils/api'
import { canEditRoutinePlan, isRoutineReceiver } from '@/utils/user'
import { displayItemNames, displayMonitorPlace } from '@/utils/siteMonitorMap'

export default {
  data() {
    const y = new Date().getFullYear()
    return {
      loading: false,
      keyword: '',
      year: y,
      years: [y, y - 1, y - 2],
      rows: [],
      tab: 'all'
    }
  },
  computed: {
    pageTitle() {
      return this.$route.meta.title || '周期任务查询'
    },
    showPlanTab() {
      return isRoutineReceiver()
    },
    displayRows() {
      if (this.tab !== 'pending') return this.rows
      return this.rows.filter(row => this.canPlanRow(row))
    }
  },
  watch: {
    '$route.path': {
      immediate: true,
      handler() {
        const def = this.$route.meta.defaultTab
        if (def) this.tab = def
        else if (this.$route.query.tab) this.tab = this.$route.query.tab
      }
    }
  },
  mounted() { this.load() },
  methods: {
    onTabChange() {
      if (this.$route.path === '/routine-plans') {
        this.$router.replace({ path: '/routine-plans', query: { tab: this.tab } })
      }
    },
    ztLabel(v) {
      const m = {
        new: '新建',
        pending_audit: '待审核',
        audit_pass: '待周期安排',
        plan_ready: '安排中',
        dispatched: '已派遣',
        sampling: '采样中',
        handover: '样品交接',
        lab_ready: '待理化',
        labing: '理化中',
        lab_done: '理化完成',
        reporting: '编报告中',
        done: '已完成',
        pending: '待分配',
        doing: '检测中'
      }
      return m[v] || v || '—'
    },
    ztTag(v) {
      return {
        pending_audit: 'warning',
        audit_pass: 'warning',
        plan_ready: 'info',
        dispatched: 'success',
        sampling: 'warning',
        handover: 'warning',
        lab_ready: 'primary',
        labing: 'primary',
        lab_done: 'success',
        done: 'success',
        new: 'info'
      }[v] || 'info'
    },
    fmtPlace(it) {
      return displayMonitorPlace(it)
    },
    fmtItems(it) {
      return displayItemNames(it) || '—'
    },
    freqText(it) {
      if (it.freq_label) return it.freq_label
      const fc = it.freq_count || 1
      const pc = it.periods_count || 1
      return `${fc}次/期，共${pc}期`
    },
    canPlanRow(row) {
      if (!row || !['audit_pass', 'plan_ready'].includes(row.xmzt)) return false
      if (row.can_edit !== undefined) return !!row.can_edit
      return canEditRoutinePlan(row)
    },
    showDispatchLink(row) {
      const zt = row && row.xmzt
      return ['dispatched', 'sampling', 'handover', 'lab_ready', 'labing', 'lab_done', 'reporting', 'done'].includes(zt)
    },
    goPlan(row) {
      this.$router.push('/routine/plan/' + encodeURIComponent(row.xmbh))
    },
    goHub(row) {
      this.$router.push({ path: '/task-track', query: { xmbh: row.xmbh } })
    },
    async load() {
      this.loading = true
      try {
        const res = await api.get('yn_routine_query_list', { year: this.year, key: this.keyword })
        this.rows = res.data || []
      } finally { this.loading = false }
    }
  }
}
</script>

<style scoped>
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px; }
.toolbar { display: flex; gap: 8px; }
.item-line { font-size: 12px; line-height: 1.6; color: #606266; }
.empty-hint { margin-bottom: 12px; }
.muted { color: #c0c4cc; font-size: 12px; }
</style>
